// src/stations/comprehension/factDiver.ts — KS2 Fact Diver: Rapid Retrieval Station (Domain 2b)
// Implements FR-ENG-005, NFR-TOUCH-001, NFR-STYLE-001.
// Tudor reads short non-fiction marine paragraphs (<=80 words) and answers >=3 targeted fact-check questions.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import { ComprehensionStorage, type RetrievalSession } from '@storage/comprehensionStorage';
import { StationProgression } from '@storage/stationProgression';
import { DiscTimer } from '@ui/components/discTimer';
import { BrainBreak } from '@ui/components/brainBreak';
import { SessionGuard } from '@storage/sessionGuard';
import type { ClaweyHud } from '@ui/components/claweyHud';

export interface FactQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface FactCardData {
  id: string;
  title: string;
  wordCount: number;
  text: string;
  illustration: string;
  questions: FactQuestion[];
}

export const FACT_DIVER_CARDS: FactCardData[] = [
  {
    id: "fact-01-lobster-molting",
    title: "The Lobster's Secret Molt",
    wordCount: 55,
    text: "Before shedding its rigid armor, a European lobster absorbs calcium from its old shell to conserve minerals. It then pumps seawater into its body until the shell splits along the back line. Soft and vulnerable, the lobster crawls out backwards and hides within a dark crevice for several weeks until its new carapace hardens completely.",
    illustration: "🦞",
    questions: [
      {
        id: "q-fact-01-1",
        question: "What mineral does the lobster absorb from its old shell before shedding?",
        options: ["Calcium", "Iron", "Magnesium", "Potassium"],
        correctIndex: 0,
        explanation: "According to the text, the lobster absorbs calcium from its old shell to conserve minerals."
      },
      {
        id: "q-fact-01-2",
        question: "How does the lobster crawl out of its split shell?",
        options: ["Backwards", "Head first", "Sideways", "Upside down"],
        correctIndex: 0,
        explanation: "The passage specifically states: 'the lobster crawls out backwards and hides within a dark crevice'."
      },
      {
        id: "q-fact-01-3",
        question: "Where does the lobster shelter while waiting for its new carapace to harden?",
        options: ["Within a dark crevice", "On open sandy beaches", "At the water surface", "Inside a floating kelp raft"],
        correctIndex: 0,
        explanation: "The text states it hides within a dark crevice for several weeks."
      }
    ]
  },
  {
    id: "fact-02-mantis-punch",
    title: "The Peacock Mantis Strike",
    wordCount: 59,
    text: "The peacock mantis shrimp strikes with spring-loaded dactyl clubs that reach speeds of fifty miles per hour underwater. This lightning-fast acceleration boils surrounding seawater, generating vapor cavitation bubbles. When these tiny bubbles violently collapse, they unleash a secondary flash of light and heat reaching thousands of degrees, delivering a double blow that shatters thick snail shells and aquarium glass.",
    illustration: "🥊",
    questions: [
      {
        id: "q-fact-02-1",
        question: "What speed do the mantis shrimp's clubs reach underwater?",
        options: ["Fifty miles per hour", "Twenty miles per hour", "One hundred miles per hour", "Ten miles per hour"],
        correctIndex: 0,
        explanation: "The text states the clubs reach speeds of fifty miles per hour underwater."
      },
      {
        id: "q-fact-02-2",
        question: "What type of bubbles are generated by the strike's extreme acceleration?",
        options: ["Vapor cavitation bubbles", "Soap bubbles", "Carbon dioxide bubbles", "Oxygen bubbles"],
        correctIndex: 0,
        explanation: "The passage notes that the acceleration boils surrounding water, generating vapor cavitation bubbles."
      },
      {
        id: "q-fact-02-3",
        question: "What happens when the tiny cavitation bubbles collapse?",
        options: ["They unleash a secondary flash of light and heat", "They freeze the target solid", "They turn into pearls", "They float silently to the surface"],
        correctIndex: 0,
        explanation: "The text explains that collapsing bubbles unleash a secondary flash of light and heat."
      }
    ]
  },
  {
    id: "fact-03-barnacle-cement",
    title: "Nature's Strongest Underwater Glue",
    wordCount: 56,
    text: "Acorn barnacles spend their adult lives permanently glued head-first onto wave-swept shoreline cliffs. Their specialized glands secrete an extraordinary waterproof cement made of fibrous protein chains. This natural adhesive resists bacterial decay, acidic seawater, and shear forces exceeding four hundred pounds per square inch, making it stronger than modern synthetic construction epoxies engineered by human scientists.",
    illustration: "🐚",
    questions: [
      {
        id: "q-fact-03-1",
        question: "How do acorn barnacles attach themselves onto shoreline cliffs?",
        options: ["Head-first using waterproof cement", "By their tails using suction cups", "Using sharp teeth", "With magnetic claws"],
        correctIndex: 0,
        explanation: "The passage says barnacles spend their lives permanently glued head-first onto cliffs."
      },
      {
        id: "q-fact-03-2",
        question: "What biological molecule makes up the barnacle's waterproof cement?",
        options: ["Fibrous protein chains", "Sticky sugar crystals", "Hard limestone paste", "Seaweed gelatin"],
        correctIndex: 0,
        explanation: "The text states their glands secrete cement made of fibrous protein chains."
      },
      {
        id: "q-fact-03-3",
        question: "What shear force does the natural barnacle adhesive withstand?",
        options: ["Exceeding 400 pounds per square inch", "Up to 50 pounds per square inch", "Exactly 100 pounds per square inch", "Over 1,000 tons"],
        correctIndex: 0,
        explanation: "The passage states it resists shear forces exceeding four hundred pounds per square inch."
      }
    ]
  },
  {
    id: "fact-04-cuttlefish-camouflage",
    title: "The Cuttlefish's Color Cloak",
    wordCount: 51,
    text: "The common cuttlefish possesses millions of microscopic pigment cells called chromatophores embedded in its skin. By flexing tiny surrounding muscle fibers, it expands or contracts these colored sacks within milliseconds. It can instantly match coarse beach pebbles, bright green sea lettuce, or dark basalt boulders to vanish from seals and sharks.",
    illustration: "🦑",
    questions: [
      {
        id: "q-fact-04-1",
        question: "What microscopic pigment cells allow cuttlefish to change colour?",
        options: ["Chromatophores", "Chloroplasts", "Scales", "Enamel"],
        correctIndex: 0,
        explanation: "The passage explains cuttlefish possess microscopic pigment cells called chromatophores."
      },
      {
        id: "q-fact-04-2",
        question: "What body structures flex to expand or contract the pigment sacks?",
        options: ["Tiny surrounding muscle fibers", "Bone joints", "Suction tentacles", "Fins"],
        correctIndex: 0,
        explanation: "The text notes that flexing tiny surrounding muscle fibers expands or contracts the sacks."
      },
      {
        id: "q-fact-04-3",
        question: "How quickly can a cuttlefish change its skin appearance?",
        options: ["Within milliseconds", "Over several hours", "After two days", "Only when asleep"],
        correctIndex: 0,
        explanation: "The text states the cuttlefish expands or contracts colored sacks within milliseconds."
      }
    ]
  },
  {
    id: "fact-05-lanternfish-photophores",
    title: "Lanternfish Counter-Illumination",
    wordCount: 51,
    text: "Lanternfish dwell in the ocean's twilight zone where sunlight is faint. Along their bellies run rows of glowing photophores produced by symbiotic bacteria. By matching the intensity and color of downwelling sunlight from above, they erase their dark silhouettes from hungry predators swimming underneath, creating a living invisibility cloak called counter-illumination.",
    illustration: "🐟",
    questions: [
      {
        id: "q-fact-05-1",
        question: "What are the glowing light organs along a lanternfish's belly called?",
        options: ["Photophores", "Dactyl clubs", "Carapaces", "Pincers"],
        correctIndex: 0,
        explanation: "The passage states that along their bellies run rows of glowing photophores."
      },
      {
        id: "q-fact-05-2",
        question: "What produces the light inside these specialized belly organs?",
        options: ["Symbiotic bacteria", "Electric shocks", "Chemical acid", "Solar rays"],
        correctIndex: 0,
        explanation: "The text states photophores are produced by symbiotic bacteria."
      },
      {
        id: "q-fact-05-3",
        question: "What camouflage technique erases the fish's silhouette from below?",
        options: ["Counter-illumination", "Thermal blinding", "False ink clouds", "Reflective armor"],
        correctIndex: 0,
        explanation: "The passage identifies this living invisibility cloak as counter-illumination."
      }
    ]
  },
  {
    id: "fact-06-vampire-squid",
    title: "The Vampire Squid's Velvet Cape",
    wordCount: 52,
    text: "Living in the oxygen minimum zone over two thousand feet deep, the vampire squid possesses the lowest metabolic rate of any cephalopod. Instead of hunting live prey, it unfurls two thin retractile sensory filaments to collect marine snow consisting of dead plankton and organic detritus falling from the sunlit waters far above.",
    illustration: "🐙",
    questions: [
      {
        id: "q-fact-06-1",
        question: "What extreme oceanic habitat does the vampire squid inhabit?",
        options: ["The oxygen minimum zone", "Boiling hot springs", "Sunny coral shoals", "Warm sandy beaches"],
        correctIndex: 0,
        explanation: "The text states the vampire squid lives in the oxygen minimum zone over two thousand feet deep."
      },
      {
        id: "q-fact-06-2",
        question: "What does the vampire squid feed upon in the deep ocean?",
        options: ["Marine snow and organic detritus", "Giant live crabs", "Flying fish", "Fresh seagrass"],
        correctIndex: 0,
        explanation: "The passage describes it collecting marine snow consisting of dead plankton and organic detritus."
      },
      {
        id: "q-fact-06-3",
        question: "What special feature allows it to survive with so little oxygen?",
        options: ["The lowest metabolic rate of any cephalopod", "Six beating hearts", "Iron lungs", "Rapid swimming bursts"],
        correctIndex: 0,
        explanation: "The text notes it possesses the lowest metabolic rate of any cephalopod."
      }
    ]
  },
  {
    id: "fact-07-anglerfish-esca",
    title: "The Anglerfish's Glowing Esca",
    wordCount: 51,
    text: "In the midnight abyss where pitch blackness reigns, the female anglerfish wields a modified dorsal fin spine tipped with a bioluminescent bulb known as the esca. Millions of glowing bacteria illuminate the lure like a lantern. Curious deep-sea fish swim close to investigate before being trapped in her cavernous, needle-lined jaws.",
    illustration: "🎣",
    questions: [
      {
        id: "q-fact-07-1",
        question: "What is the bioluminescent bulb at the tip of the anglerfish's lure called?",
        options: ["The esca", "The carapace", "The dactyl", "The siphon"],
        correctIndex: 0,
        explanation: "The text states the modified dorsal fin spine is tipped with a bioluminescent bulb known as the esca."
      },
      {
        id: "q-fact-07-2",
        question: "What modified body part forms the anglerfish's fishing rod?",
        options: ["A modified dorsal fin spine", "A front walking leg", "Her lower jaw bone", "An extended whisker"],
        correctIndex: 0,
        explanation: "The passage mentions the female anglerfish wields a modified dorsal fin spine."
      },
      {
        id: "q-fact-07-3",
        question: "What lights up the glowing bulb in the dark abyss?",
        options: ["Millions of glowing bacteria", "Reflected sunlight", "Electric eels", "Phosphorescent coral"],
        correctIndex: 0,
        explanation: "The text explains that millions of glowing bacteria illuminate the lure like a lantern."
      }
    ]
  },
  {
    id: "fact-08-giant-isopod",
    title: "Giant Deep-Sea Isopods",
    wordCount: 51,
    text: "Giant isopods dwell on the muddy seafloor more than three thousand feet beneath the waves. Growing up to fourteen inches long due to deep-sea gigantism, they have thick segmented exoskeletons and fourteen jointed walking legs. Because food is scarce, their slow metabolism allows them to endure years of fasting between meals.",
    illustration: "🛡️",
    questions: [
      {
        id: "q-fact-08-1",
        question: "What biological phenomenon causes giant isopods to reach fourteen inches?",
        options: ["Deep-sea gigantism", "Constant feeding", "Warm tropical water", "Rapid swimming"],
        correctIndex: 0,
        explanation: "The passage states they grow up to fourteen inches long due to deep-sea gigantism."
      },
      {
        id: "q-fact-08-2",
        question: "How many jointed walking legs does a giant isopod possess?",
        options: ["Fourteen jointed walking legs", "Eight tentacles", "Six insect legs", "Twenty claws"],
        correctIndex: 0,
        explanation: "The text specifically notes they have fourteen jointed walking legs."
      },
      {
        id: "q-fact-08-3",
        question: "How do giant isopods survive long periods when food is scarce?",
        options: ["Their slow metabolism allows years of fasting", "They hibernate in ice", "They swim to the surface", "They eat sea sand"],
        correctIndex: 0,
        explanation: "The passage says their slow metabolism allows them to endure years of fasting between meals."
      }
    ]
  },
  {
    id: "fact-09-pompeii-worm",
    title: "Hydrothermal Vent Pompeii Worms",
    wordCount: 51,
    text: "Pompeii worms thrive on the mineral chimneys of deep hydrothermal vents, enduring scorching temperatures exceeding eighty degrees Celsius. They produce thick fleece-like mats of symbiotic bacteria on their backs. These specialized bacteria consume toxic hydrogen sulfide from volcanic fluid and form an insulating protective coat guarding the worm against thermal scalds.",
    illustration: "🌋",
    questions: [
      {
        id: "q-fact-09-1",
        question: "Where do Pompeii worms build their homes in the deep sea?",
        options: ["Mineral chimneys of hydrothermal vents", "Floating seaweed beds", "Soft shallow mudflats", "Cold freshwater lakes"],
        correctIndex: 0,
        explanation: "The text notes Pompeii worms thrive on the mineral chimneys of deep hydrothermal vents."
      },
      {
        id: "q-fact-09-2",
        question: "What scorching temperature can Pompeii worms endure?",
        options: ["Exceeding eighty degrees Celsius", "Exactly twenty degrees Celsius", "Freezing zero degrees", "Ten degrees Celsius"],
        correctIndex: 0,
        explanation: "The passage states they endure scorching temperatures exceeding eighty degrees Celsius."
      },
      {
        id: "q-fact-09-3",
        question: "How do symbiotic bacteria on their backs protect the worm?",
        options: ["They form an insulating coat and consume toxic chemicals", "They produce bright flashes", "They poison attackers", "They dig escape tunnels"],
        correctIndex: 0,
        explanation: "The text notes they consume toxic hydrogen sulfide and form an insulating protective coat."
      }
    ]
  },
  {
    id: "fact-10-mariana-snailfish",
    title: "The Mariana Trench Snailfish",
    wordCount: 52,
    text: "The Mariana snailfish reigns at five miles beneath the surface in the freezing Hadal Trench. Enduring crushing pressures exceeding one thousand atmospheres, its gelatinous scaleless body lacks air cavities that would implode. Its cellular membranes incorporate specialized organic molecules called piezolytes that stabilize proteins under the tremendous pressure of the deep abyss.",
    illustration: "🌊",
    questions: [
      {
        id: "q-fact-10-1",
        question: "How deep beneath the ocean surface does the Mariana snailfish live?",
        options: ["Five miles beneath the surface", "Two hundred meters", "Ten feet below tide", "Exactly one mile"],
        correctIndex: 0,
        explanation: "The passage notes the Mariana snailfish reigns at five miles beneath the surface."
      },
      {
        id: "q-fact-10-2",
        question: "Why does the snailfish's body lack air cavities like swim bladders?",
        options: ["Air cavities would implode under crushing pressure", "To help it breathe air", "Because it hunts on land", "To store fresh water"],
        correctIndex: 0,
        explanation: "The text states its body lacks air cavities that would implode under crushing pressure."
      },
      {
        id: "q-fact-10-3",
        question: "What organic molecules stabilize the snailfish's proteins under pressure?",
        options: ["Piezolytes", "Calcium carbonate", "Hemoglobin", "Salt crystals"],
        correctIndex: 0,
        explanation: "The passage explains specialized organic molecules called piezolytes stabilize proteins under pressure."
      }
    ]
  }
];

export class FactDiverStation {
  private container: HTMLElement;
  private hud?: ClaweyHud;
  private cardIndex: number = 0;
  private currentQuestionIndex: number = 0;
  private selectedOptionIndex: number | null = null;
  private hasAnsweredCurrent: boolean = false;
  private isCorrectCurrent: boolean = false;
  private sessionCorrectCount: number = 0;
  private timedModeEnabled: boolean = false;
  private timerSecondsRemaining: number = 60;
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private isZenMode: boolean = false;
  private discTimer: DiscTimer | null = null;

  constructor(hud?: ClaweyHud, initialCardIndex: number = 0) {
    this.container = document.createElement('section');
    this.container.id = 'fact-diver-station';
    this.container.className = 'station-container';
    this.hud = hud;
    this.cardIndex = Math.max(0, Math.min(initialCardIndex, FACT_DIVER_CARDS.length - 1));
  }

  setHud(hud: ClaweyHud): void {
    this.hud = hud;
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    const card = FACT_DIVER_CARDS[this.cardIndex]!;
    const isMuted = soundEngine.isMuted();

    this.container.innerHTML = `
      <!-- Header -->
      <div class="morris-card morris-card-gold" style="margin-bottom: 20px; padding: 24px 20px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px;">
          <div style="flex: 1; min-width: 260px;">
            <div style="display: inline-flex; align-items: center; gap: 8px; padding: 4px 12px; border: 1px solid ${tokens.color.gildedBrass}; border-radius: 20px; font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; margin-bottom: 10px; background: rgba(255, 246, 229, 0.7);">
              <span>🤿</span> KS2 Reading Domain 2b · Rapid Retrieval
            </div>
            <h1 style="font-size: 2.1rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 8px;">
              Fact Diver: Retrieval Station
            </h1>
            <p style="font-size: 1rem; color: #3A4D66; max-width: 600px; line-height: 1.6; margin: 0;">
              Dive through short crustacean ecology paragraphs (&le;80 words) and retrieve exact facts quickly.
            </p>
          </div>

          <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
            <!-- Timer / Mode Selector -->
            <button id="btn-toggle-timer" class="touch-target" style="padding: 8px 14px; border-radius: 20px; font-size: 0.85rem; font-weight: 800; background: ${this.timedModeEnabled ? tokens.color.gildedBrass : 'rgba(26, 54, 93, 0.08)'}; border: 1px solid rgba(26, 54, 93, 0.2); color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              ${this.timedModeEnabled ? `⏱️ 60s Challenge: ${this.isZenMode ? '🧘 Zen' : this.timerSecondsRemaining + 's'}` : '⏱️ Enable 60s Challenge'}
            </button>

            ${this.timedModeEnabled ? `
              <!-- Visual Disc Timer Mount (FR-ADHD-001) -->
              <div id="fact-disc-mount" style="display: flex; align-items: center; justify-content: center; min-width: 48px; min-height: 48px;"></div>
              <button id="btn-fact-zen-toggle" class="touch-target" style="padding: 8px 12px; border-radius: 20px; font-size: 0.85rem; font-weight: 800; background: #FFFDF9; border: 1px solid ${tokens.color.gildedBrass};">
                ${this.isZenMode ? '🧘 Zen: ON' : '⏱️ Zen: OFF'}
              </button>
            ` : ''}

            <button id="btn-fact-sound-toggle" class="touch-target" style="padding: 8px 14px; border-radius: 20px; font-size: 0.85rem; font-weight: 800; background: rgba(26, 54, 93, 0.08); border: 1px solid rgba(26, 54, 93, 0.2); color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              ${isMuted ? '🔇 Off' : '🔊 Sound'}
            </button>
          </div>
        </div>
      </div>

      <!-- Main Stage -->
      <section class="morris-card" style="padding: 32px 28px; margin-bottom: 24px;">
        <!-- 10-Tier Set Carousel Navigation (FR-PROG-001) -->
        <div style="margin-bottom: 20px;">
          <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;">
            <span>🧭 Depth Zone Expeditions · 10 Progressive Challenge Sets</span>
            <span style="color: ${tokens.color.morrisIndigo};">
              Mastered: ${StationProgression.getCompletedSetCount('fact-diver')} of 10 Sets
            </span>
          </div>
          <div style="display: flex; gap: 8px; overflow-x: auto; padding: 4px 2px 10px; scrollbar-width: thin;">
            ${FACT_DIVER_CARDS.map((_, idx) => {
              const setNum = idx + 1;
              const isUnlocked = StationProgression.isSetUnlocked('fact-diver', setNum);
              const isCurrent = this.cardIndex === idx;
              const prog = StationProgression.getProgression('fact-diver');
              const comp = prog.completedSets[setNum];
              const starsStr = comp ? ' ' + '★'.repeat(comp.stars) : '';

              return `
                <button
                  class="touch-target fact-set-pill-btn"
                  data-set-idx="${idx}"
                  ${!isUnlocked ? 'disabled style="opacity: 0.45; cursor: not-allowed;"' : ''}
                  style="
                    padding: 8px 14px;
                    font-size: 0.85rem;
                    font-weight: 800;
                    border-radius: 20px;
                    white-space: nowrap;
                    border: 1.5px solid ${isCurrent ? tokens.color.gildedBrass : 'rgba(26,54,93,0.2)'};
                    background: ${isCurrent ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
                    color: ${tokens.color.morrisIndigo};
                    cursor: ${isUnlocked ? 'pointer' : 'not-allowed'};
                  "
                >
                  ${!isUnlocked ? '🔒 ' : ''}Set ${setNum}${starsStr}
                </button>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Card Switcher & Metadata -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 18px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="font-size: 2.2rem;">${card.illustration}</span>
            <div>
              <h2 style="font-size: 1.8rem; color: ${tokens.color.morrisIndigo}; margin: 0;">${card.title}</h2>
              <span style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">
                Paragraph Word Count: ${card.wordCount} words (&le;80 limit) · Set ${this.cardIndex + 1} of 10
              </span>
            </div>
          </div>

          <div style="display: flex; gap: 8px; align-items: center;">
            <button id="btn-prev-fact" class="touch-target" style="padding: 8px 14px; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 20px;" ${this.cardIndex === 0 ? 'disabled style="opacity: 0.4;"' : ''}>
              &larr; Prev
            </button>
            <span style="font-size: 0.9rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">
              Card ${this.cardIndex + 1} of ${FACT_DIVER_CARDS.length}
            </span>
            <button id="btn-next-fact" class="touch-target" style="padding: 8px 14px; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 20px;" ${this.cardIndex === FACT_DIVER_CARDS.length - 1 ? 'disabled style="opacity: 0.4;"' : ''}>
              Next &rarr;
            </button>
          </div>
        </div>

        <!-- The <=80-Word Paragraph Card -->
        <div style="padding: 24px; background: rgba(255, 253, 249, 0.98); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px; font-size: 1.25rem; line-height: 1.7; color: #1A365D; margin-bottom: 28px; box-shadow: 0 4px 16px rgba(10, 25, 47, 0.06);">
          <div style="font-size: 0.75rem; color: ${tokens.color.madderCoral}; font-weight: 800; text-transform: uppercase; margin-bottom: 8px;">
            📖 Reference Text (Retrieve facts from here)
          </div>
          <p style="margin: 0; font-family: ${tokens.font.body};">
            ${card.text}
          </p>
        </div>

        <!-- Fact-Check Question -->
        ${this.renderQuestionSection(card)}
      </section>

      <!-- Bottom Navigation -->
      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px;">
        <a href="#/context-lens" class="touch-target" style="padding: 10px 20px; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 12px; text-decoration: none; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
          🔎 Switch to Context Lens (Vocabulary 2a/2g)
        </a>
        <a href="#/read" class="touch-target" style="padding: 10px 20px; border: 1.5px solid ${tokens.color.seaweedSage}; background: #FFFDF9; border-radius: 12px; text-decoration: none; color: ${tokens.color.seaweedSage}; font-weight: 700;">
          🔍 Switch to Clue Hunter (Domain 2d) &rarr;
        </a>
      </div>
    `;

    // Mount Visual Disc Timer if timed mode is enabled
    const discMount = this.container.querySelector<HTMLElement>('#fact-disc-mount');
    if (discMount && this.discTimer && this.timedModeEnabled) {
      discMount.innerHTML = '';
      discMount.appendChild(this.discTimer.getElement());
    }

    this.attachEventListeners();
  }

  private renderQuestionSection(card: FactCardData): string {
    const q = card.questions[this.currentQuestionIndex];
    if (!q) return '';

    return `
      <div style="padding: 24px; background: rgba(250, 246, 237, 0.9); border: 1.5px solid ${tokens.color.morrisIndigo}; border-radius: 14px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <span style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; text-transform: uppercase;">
            Fact Check: Question ${this.currentQuestionIndex + 1} of ${card.questions.length} (&ge;3 Questions per exercise)
          </span>
          <span style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800;">
            Score: ${this.sessionCorrectCount} of ${card.questions.length}
          </span>
        </div>

        <h3 style="font-size: 1.25rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 18px; line-height: 1.5;">
          ${q.question}
        </h3>

        <!-- Options -->
        <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px;">
          ${q.options
            .map((opt, idx) => {
              const isSelected = this.selectedOptionIndex === idx;
              let bg = '#FFFDF9';
              let borderColor = 'rgba(26, 54, 93, 0.2)';
              let icon = '🔘';

              if (this.hasAnsweredCurrent) {
                if (idx === q.correctIndex) {
                  bg = 'rgba(61, 90, 69, 0.15)';
                  borderColor = tokens.color.seaweedSage;
                  icon = '✅';
                } else if (isSelected) {
                  bg = 'rgba(255, 107, 74, 0.15)';
                  borderColor = tokens.color.madderCoral;
                  icon = '❌';
                }
              } else if (isSelected) {
                bg = 'rgba(229, 169, 60, 0.15)';
                borderColor = tokens.color.gildedBrass;
                icon = '🔵';
              }

              return `
                <button
                  class="touch-target btn-fact-option"
                  data-index="${idx}"
                  style="display: flex; align-items: center; gap: 12px; text-align: left; padding: 14px 18px; font-size: 1.05rem; border: 2px solid ${borderColor}; background: ${bg}; border-radius: 12px; cursor: pointer; transition: all 0.2s ease;"
                  ${this.hasAnsweredCurrent ? 'disabled' : ''}
                >
                  <span style="font-size: 1.2rem;">${icon}</span>
                  <span style="flex: 1; color: ${tokens.color.morrisIndigo}; font-weight: 600;">${opt}</span>
                </button>
              `;
            })
            .join('')}
        </div>

        <!-- Action / Explanation -->
        ${this.renderQuestionFooter(card, q)}
      </div>
    `;
  }

  private renderQuestionFooter(card: FactCardData, q: FactQuestion): string {
    if (!this.hasAnsweredCurrent) {
      const canSubmit = this.selectedOptionIndex !== null;
      return `
        <button
          id="btn-fact-submit"
          class="touch-target btn-primary"
          style="padding: 14px 28px; font-size: 1.1rem; width: 100%; max-width: 320px;"
          ${canSubmit ? '' : 'disabled style="opacity: 0.5;"'}
        >
          Verify Fact &rarr;
        </button>
      `;
    }

    const isLast = this.currentQuestionIndex >= card.questions.length - 1;

    return `
      <div style="padding: 14px 18px; background: rgba(255, 253, 249, 0.95); border-radius: 10px; border-left: 4px solid ${this.isCorrectCurrent ? tokens.color.seaweedSage : tokens.color.madderCoral}; margin-bottom: 16px;">
        <div style="font-weight: 800; font-size: 1rem; color: ${this.isCorrectCurrent ? tokens.color.seaweedSage : tokens.color.madderCoral}; margin-bottom: 4px;">
          ${this.isCorrectCurrent ? '✓ Exact Retrieval!' : '🔍 Look back at the text:'}
        </div>
        <p style="margin: 0; font-size: 1rem; line-height: 1.5; color: ${tokens.color.morrisIndigo};">
          ${q.explanation}
        </p>
      </div>

      <div style="display: flex; gap: 12px; flex-wrap: wrap; align-items: center;">
        ${!isLast ? `
          <button id="btn-fact-next-q" class="touch-target btn-primary" style="padding: 12px 24px; font-size: 1.05rem;">
            Next Question (${this.currentQuestionIndex + 2} of ${card.questions.length}) &rarr;
          </button>
        ` : `
          <div style="width: 100%; padding: 14px 18px; background: rgba(229, 169, 60, 0.15); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 12px; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
            <div>
              <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
                🌟 Set ${this.cardIndex + 1} Conquered!
              </div>
              <div style="font-size: 1.1rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
                Score: ${this.sessionCorrectCount} of ${card.questions.length} · +25 Explorer Points Awarded!
              </div>
            </div>
            <div style="font-size: 1.4rem;">
              ${this.sessionCorrectCount === card.questions.length ? '⭐⭐⭐' : this.sessionCorrectCount >= 2 ? '⭐⭐' : '⭐'}
            </div>
          </div>
          <button id="btn-fact-next-card" class="touch-target btn-primary" style="padding: 12px 24px; font-size: 1.05rem;">
            ${this.cardIndex < FACT_DIVER_CARDS.length - 1 ? `Embark on Set ${this.cardIndex + 2} &rarr;` : 'Restart Fact Diver Sets 🔄'}
          </button>
          <button id="btn-fact-brain-break" class="touch-target" style="padding: 12px 20px; font-size: 1rem; border: 1.5px solid ${tokens.color.seaweedSage}; background: rgba(61, 90, 69, 0.08); color: ${tokens.color.seaweedSage}; border-radius: 12px; font-weight: 800; cursor: pointer;">
            🧘 30s Brain Break
          </button>
        `}
      </div>
    `;
  }

  private attachEventListeners(): void {
    // Set pill buttons (10-Tier Navigation)
    const setPills = this.container.querySelectorAll<HTMLButtonElement>('.fact-set-pill-btn');
    setPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const setIdx = parseInt(pill.getAttribute('data-set-idx') ?? '0', 10);
        soundEngine.playTap();
        this.changeCard(setIdx);
      });
    });

    // Sound toggle
    const soundToggle = this.container.querySelector<HTMLButtonElement>('#btn-fact-sound-toggle');
    soundToggle?.addEventListener('click', () => {
      soundEngine.toggleMute();
      this.updateView();
    });

    // Timer challenge toggle
    const timerBtn = this.container.querySelector<HTMLButtonElement>('#btn-toggle-timer');
    timerBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.toggleTimedMode();
    });

    const zenBtn = this.container.querySelector<HTMLButtonElement>('#btn-fact-zen-toggle');
    zenBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.isZenMode = !this.isZenMode;
      if (this.discTimer) {
        this.discTimer.setZenMode(this.isZenMode);
      }
      this.updateView();
    });

    const brainBreakBtn = this.container.querySelector<HTMLButtonElement>('#btn-fact-brain-break');
    brainBreakBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      const brainBreak = new BrainBreak({
        onComplete: () => {
          if (this.hud) {
            this.hud.say('Naturalist focus restored! Ready to dive again, Tudor.', 'celebrating');
          }
        },
      });
      document.body.appendChild(brainBreak.getElement());
    });

    // Card navigation
    const prevBtn = this.container.querySelector<HTMLButtonElement>('#btn-prev-fact');
    prevBtn?.addEventListener('click', () => {
      if (this.cardIndex > 0) {
        soundEngine.playTap();
        this.changeCard(this.cardIndex - 1);
      }
    });

    const nextBtn = this.container.querySelector<HTMLButtonElement>('#btn-next-fact');
    nextBtn?.addEventListener('click', () => {
      if (this.cardIndex < FACT_DIVER_CARDS.length - 1) {
        soundEngine.playTap();
        this.changeCard(this.cardIndex + 1);
      }
    });

    // Option selection
    const optionBtns = this.container.querySelectorAll<HTMLButtonElement>('.btn-fact-option');
    optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.hasAnsweredCurrent) return;
        soundEngine.playTap();
        this.selectedOptionIndex = parseInt(btn.getAttribute('data-index') ?? '0', 10);
        this.updateView();
      });
    });

    // Submit
    const submitBtn = this.container.querySelector<HTMLButtonElement>('#btn-fact-submit');
    submitBtn?.addEventListener('click', () => {
      if (this.selectedOptionIndex === null) return;
      this.processAnswer();
    });

    // Next question
    const nextQBtn = this.container.querySelector<HTMLButtonElement>('#btn-fact-next-q');
    nextQBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.currentQuestionIndex += 1;
      this.selectedOptionIndex = null;
      this.hasAnsweredCurrent = false;
      this.isCorrectCurrent = false;
      this.updateView();
    });

    // Next card / finish
    const nextCardBtn = this.container.querySelector<HTMLButtonElement>('#btn-fact-next-card');
    nextCardBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      const nextIdx = (this.cardIndex + 1) % FACT_DIVER_CARDS.length;
      this.changeCard(nextIdx);
    });
  }

  private processAnswer(): void {
    const card = FACT_DIVER_CARDS[this.cardIndex]!;
    const q = card.questions[this.currentQuestionIndex]!;
    const isCorrect = this.selectedOptionIndex === q.correctIndex;

    this.hasAnsweredCurrent = true;
    this.isCorrectCurrent = isCorrect;

    if (isCorrect) {
      this.sessionCorrectCount += 1;
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say('Pinpoint accuracy, Tudor! You pulled the exact fact straight from the field card.', 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud) {
        this.hud.say('Scan the lines once more, Tudor. The answer is directly stated in the text.', 'thinking');
      }
    }

    // If last question on card, persist session and check session guard
    if (this.currentQuestionIndex >= card.questions.length - 1) {
      const timeSpent = 60 - this.timerSecondsRemaining;
      const session: RetrievalSession = {
        passageId: card.id,
        correctCount: this.sessionCorrectCount,
        totalQuestions: card.questions.length,
        timeSpentSeconds: timeSpent,
        timestamp: Date.now(),
      };
      ComprehensionStorage.recordRetrievalSession(session);
      SessionGuard.recordPracticeTime(timeSpent);

      // Record set completion in StationProgression (FR-PROG-001)
      StationProgression.completeSet(
        'fact-diver',
        this.cardIndex + 1,
        this.sessionCorrectCount,
        card.questions.length
      );

      if (SessionGuard.isDailyLimitReached()) {
        const restModal = SessionGuard.createRestWarningModal(() => {});
        document.body.appendChild(restModal);
      }
    }

    this.updateView();
  }

  private changeCard(newIndex: number): void {
    this.cardIndex = newIndex;
    this.currentQuestionIndex = 0;
    this.selectedOptionIndex = null;
    this.hasAnsweredCurrent = false;
    this.isCorrectCurrent = false;
    this.sessionCorrectCount = 0;
    this.resetTimer();
    this.updateView();
  }

  private toggleTimedMode(): void {
    this.timedModeEnabled = !this.timedModeEnabled;
    if (this.timedModeEnabled) {
      this.startTimer();
    } else {
      this.stopTimer();
    }
    this.updateView();
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerSecondsRemaining = 60;
    if (this.discTimer) {
      this.discTimer.destroy();
    }
    this.discTimer = new DiscTimer({
      durationSeconds: 60,
      size: 48,
      isZenMode: this.isZenMode,
    });
    this.discTimer.start();

    this.timerInterval = setInterval(() => {
      this.timerSecondsRemaining -= 1;
      if (this.timerSecondsRemaining <= 0) {
        this.timerSecondsRemaining = 0;
        this.stopTimer();
        if (this.hud) {
          this.hud.say('Time is up, Tudor! Let us check what facts you gathered.', 'idle');
        }
        this.updateView();
      } else {
        // Update timer button text if element is in DOM
        const timerBtn = this.container.querySelector<HTMLButtonElement>('#btn-toggle-timer');
        if (timerBtn) {
          timerBtn.textContent = `⏱️ 60s Challenge: ${this.isZenMode ? '🧘 Zen' : this.timerSecondsRemaining + 's'}`;
        }
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.discTimer) {
      this.discTimer.destroy();
      this.discTimer = null;
    }
  }

  private resetTimer(): void {
    this.stopTimer();
    if (this.timedModeEnabled) {
      this.startTimer();
    }
  }

  destroy(): void {
    this.stopTimer();
    if (this.discTimer) {
      this.discTimer.destroy();
      this.discTimer = null;
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
